import { yupResolver } from "@hookform/resolvers/yup";
import clsx from "clsx";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { number, object } from "yup";

import AutoComplete from "components/AutoComplete";
import Button from "components/Button";
import DatePicker from "components/DatePicker";
import GoogleAutoComplete from "components/GoogleAutoComplete";
import Input from "components/Input";
import Select from "components/Select";
import { FILTER_TAB_ITEMS, LOAD_LENGTH_LIST, LOAD_RADIUS_LIST } from "constants/loads";
import { EQUIPMENT_TYPES } from "constants/signup";
import { REGION_DATA } from "constants/trucks";
import { convertLoadFilterFormData } from "helpers/convertFormData";
import useCurrentLocation from "hooks/useCurrentLocation";
import useDeviceType from "hooks/useDeviceType";
import { useUserFeature } from "hooks/useUserFeature";
import { LoadSearchFilterFormData } from "models/Load";
import styles from "./style.module.scss";
import Icon from "@mdi/react";
import { mdiClose } from "@mdi/js";
import { showErrorMessage } from "helpers/toastMessage";

const loadSearchFilterFormSchema = object<Partial<LoadSearchFilterFormData>>({
  weight: number()
    .nullable()
    .transform((val, orig) => (orig === "" ? undefined : val))
    .moreThan(999, "Weight must be greater than or equal to 1000 (lbs) pounds.")
    .lessThan(100000, "Weight must be less than or equal to 100,000 (lbs) pounds.")
});

interface ILoadFilterFormProps {
  setIsScroll: React.Dispatch<React.SetStateAction<boolean>>;
  setFilter: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  toggleFilterModal: VoidFunction;
  resetPagination: VoidFunction;
}

export default function LoadFilterForm({
  setFilter,
  setIsScroll,
  toggleFilterModal,
  resetPagination
}: Readonly<ILoadFilterFormProps>) {
  const { isCarrier } = useUserFeature();
  const { isDesktop } = useDeviceType();
  const { refreshLocation } = useCurrentLocation();
  const [isDateRange, setIsDateRange] = useState(false);
  const [tabValue, setTabValue] = useState(FILTER_TAB_ITEMS[0]);
  const [prevFilters, setPrevFilters] = useState<LoadSearchFilterFormData>();

  const {
    register,
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
    formState: { errors, isLoading, isSubmitted }
  } = useForm<Partial<LoadSearchFilterFormData>>({
    resolver: yupResolver(loadSearchFilterFormSchema),
    values: {
      length: LOAD_LENGTH_LIST[0]
    }
  });

  function onReverseSearch() {
    const originStates = getValues("originState");
    const destinationStates = getValues("destinationState");
    const originRegion = getValues("originRegion") === undefined ? [] : getValues("originRegion");
    const destinationRegion =
      getValues("destinationRegion") === undefined ? [] : getValues("destinationRegion");

    setValue("originState", destinationStates || {});
    setValue("destinationState", originStates || {});
    setValue("originRegion", destinationRegion);
    setValue("destinationRegion", originRegion);
  }

  const getUserCurrentState = async () => {
    try {
      const { currentState } = await refreshLocation();
      if (currentState) {
        const state = REGION_DATA.find((region) => region.label === currentState);
        state && setValue("originRegion", [state]);
      }
    } catch {
      showErrorMessage("Error getting location");
    }
  };

  const getUserCurrentLocation = async () => {
    try {
      const { addressObject } = await refreshLocation();
      addressObject && setValue("originState", addressObject);
    } catch {
      showErrorMessage("Error getting location");
    }
  };

  const onSubmit = async (data: LoadSearchFilterFormData) => {
    if (prevFilters && JSON.stringify(prevFilters) === JSON.stringify(data)) {
      return;
    }
    const {
      originCity,
      originState,
      destinationCity,
      destinationState,
      originRadius,
      destinationRadius,
      equipmentType,
      weight,
      length,
      timeStart,
      timeEnd
    } = convertLoadFilterFormData(data, tabValue, isDateRange);
    setIsScroll(false);
    resetPagination();
    setFilter((prev) => ({
      orderBy: prev?.orderBy,
      ...(originCity && { originCity: originCity }),
      ...(originState ? { originState: originState } : { originState: "" }),
      ...(destinationCity && { destinationCity: destinationCity }),
      ...(destinationState ? { destinationState: destinationState } : { destinationState: "" }),
      originRadius: originRadius,
      destinationRadius: destinationRadius,
      ...(equipmentType.length && { trailerType: equipmentType }),
      ...(weight && { weight: parseInt(weight) }),
      ...(length === "length_Lte" && { length_Lte: 40 }),
      ...(length === "length_Gte" && { length_Gte: 40 }),
      timeStart: timeStart,
      timeEnd: timeEnd
    }));
    setPrevFilters(data);
    if (!isDesktop) toggleFilterModal();
  };

  return (
    <form className={styles.loadFilterForm} onSubmit={handleSubmit(onSubmit)}>
      {isCarrier ? (
        <ul className={styles.loadSearchTab}>
          {FILTER_TAB_ITEMS.map((item) => (
            <li key={item.id} className={styles.tabItem}>
              <button type="button" onClick={() => setTabValue(item)}>
                {item.label}
              </button>
            </li>
          ))}
          <div
            className={clsx(
              styles.tabIndicator,
              tabValue.value === "by_city" ? "translate-x-0" : "translate-x-full"
            )}></div>
        </ul>
      ) : null}

      <div className={styles.addressFields}>
        <div className={styles.originFields}>
          {tabValue.value === "by_city" && isCarrier ? (
            <div className={styles.originFieldsByCity}>
              <Controller
                name="originState"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <GoogleAutoComplete
                      value={value}
                      placeholder="Anywhere"
                      onChange={onChange}
                      label={
                        <div className="pl-2">
                          <span className="mr-2">Origin</span>
                          <button
                            type="button"
                            className="text-primary"
                            onClick={getUserCurrentLocation}>
                            Current Location
                          </button>
                        </div>
                      }
                      containerClass="flex-1"
                    />
                  );
                }}
              />
              <Controller
                name="originRadius"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    label="&nbsp;&nbsp;Radius"
                    value={value}
                    onChange={onChange}
                    options={LOAD_RADIUS_LIST}
                    placeholder="&nbsp;"
                    btnClass="desktop:!w-44 !w-24"
                  />
                )}
              />
            </div>
          ) : (
            <AutoComplete
              name="originRegion"
              label={
                <div className="pl-2">
                  <span className="mr-2">Origin</span>
                  <button type="button" className="text-primary" onClick={getUserCurrentState}>
                    Current State
                  </button>
                </div>
              }
              placeholder="Any"
              options={REGION_DATA}
              control={control}
              className="flex-1 mobile:mb-1"
              isMulti
            />
          )}
        </div>
        {isCarrier ? (
          <div className={styles.destinationFields}>
            {tabValue.value === "by_city" ? (
              <div className={styles.destinationFieldsByCity}>
                <Controller
                  name="destinationState"
                  control={control}
                  render={({ field: { value, onChange } }) => {
                    return (
                      <GoogleAutoComplete
                        value={value}
                        placeholder="Anywhere"
                        onChange={onChange}
                        label="&nbsp;&nbsp;Destination"
                        containerClass="flex-1"
                      />
                    );
                  }}
                />
                <Controller
                  name="destinationRadius"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Select
                      label="&nbsp;&nbsp;Radius"
                      value={value}
                      onChange={onChange}
                      options={LOAD_RADIUS_LIST}
                      placeholder="&nbsp;"
                      btnClass="desktop:!w-44 !w-24"
                    />
                  )}
                />
              </div>
            ) : (
              <AutoComplete
                name="destinationRegion"
                label="&nbsp;&nbsp;Destination"
                placeholder="Any"
                options={REGION_DATA}
                control={control}
                className="flex-1"
                isMulti
              />
            )}
          </div>
        ) : null}
      </div>

      <div
        className={clsx(
          styles.otherFields,
          isCarrier ? "desktop:grid-cols-4" : "desktop:grid-cols-3"
        )}>
        <AutoComplete
          name="equipmentType"
          label="&nbsp;&nbsp;Equipment Type"
          placeholder="Any"
          options={EQUIPMENT_TYPES}
          control={control}
          isMulti
        />

        {isCarrier ? (
          <div className="flex gap-2">
            <Input
              type="number"
              inputMode="numeric"
              name="weight"
              register={register}
              label="&nbsp;&nbsp;Load Weight (Max)"
              error={errors.weight}
              placeholder="Any"
              className="flex-1"
              suffixUnit="lbs"
            />

            <Controller
              name="length"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  value={value}
                  onChange={onChange}
                  label="&nbsp;&nbsp;Load Length"
                  options={LOAD_LENGTH_LIST}
                  className="flex-1"
                />
              )}
            />
          </div>
        ) : null}

        <Controller
          name="startDate"
          control={control}
          render={({ field: { value, onChange } }) => (
            <DatePicker
              value={value}
              onChange={onChange}
              label={
                !isCarrier ? (
                  "Start"
                ) : (
                  <div className={styles.startDateLabel}>
                    <span>{isDateRange ? "Start" : " Pick Up Date"}</span>
                    <button
                      type="button"
                      className={styles.dateRangeButton}
                      onClick={() => setIsDateRange((prev) => !prev)}>
                      {isDateRange ? "By Avail. Date" : "By Date Range"}
                    </button>
                  </div>
                )
              }
              labelClass="pl-2"
            />
          )}
        />

        {(!isCarrier || isDateRange) && (
          <Controller
            name="endDate"
            control={control}
            render={({ field: { value, onChange } }) => (
              <DatePicker value={value} onChange={onChange} label="&nbsp;&nbsp;End" />
            )}
          />
        )}

        {isCarrier ? (
          <button
            type="button"
            onClick={onReverseSearch}
            className={clsx(styles.reverseButton, "desktop:!hidden")}>
            Reverse Search
          </button>
        ) : null}
      </div>

      <footer className={styles.footer}>
        {isCarrier ? (
          <Button
            type="button"
            variant="outline"
            className={clsx(styles.button, "mobile:!hidden")}
            onClick={onReverseSearch}>
            Reverse
          </Button>
        ) : null}
        <Button type="submit" className={styles.button} loading={isLoading}>
          Search
        </Button>
        {isSubmitted && (
          <Button
            onClick={() => {
              resetPagination();
              setFilter({
                orderBy: "-time_posted"
              });
              reset({
                weight: undefined,
                length: undefined,
                originRegion: [],
                destinationRegion: [],
                originState: undefined,
                originRadius: undefined,
                destinationState: undefined,
                destinationRadius: undefined,
                equipmentType: [],
                startDate: "",
                endDate: ""
              });
            }}
            variant="outline"
            className={"flex-auto h-14 !text-red-600 !border-red-600"}>
            <Icon path={mdiClose} size={1} />
          </Button>
        )}
      </footer>
    </form>
  );
}
